import * as Sentry from '@sentry/vue';
import { shouldDropBookProcessedSentryEvent } from '@/utilities/bookProcessedError';
import {
    BROWSER_EXTENSION_URL_PATTERNS,
    shouldDropBrowserExtensionSentryEvent,
} from '@/utilities/browserExtensionNoise';

function createSentry({ app, router }) {
    // only run Sentry in deployed environments
    if (
        import.meta.env.VITE_ENV == 'production' ||
        import.meta.env.VITE_ENV == 'testing' ||
        import.meta.env.VITE_ENV == 'development'
    ) {
        let reportingEnvironment = import.meta.env.VITE_RELEASE_BRANCH ?? import.meta.env.VITE_ENV;

        if (reportingEnvironment == 'main') {
            reportingEnvironment = 'production';
        }

        Sentry.init({
            app,
            dsn: import.meta.env.VITE_SENTRY_DSN,
            release: import.meta.env.VITE_RELEASE ? `movepro-book@${import.meta.env.VITE_RELEASE}` : undefined,
            environment: reportingEnvironment,
            integrations: [
                Sentry.browserTracingIntegration({ router }),
                Sentry.replayIntegration({
                    networkDetailAllowUrls: [
                        new RegExp(import.meta.env.VITE_API_BASE_URL + '/customers/book/orders/[A-Z0-9]+/inventory'),
                        new RegExp(import.meta.env.VITE_API_BASE_URL + '/customers/book/[A-Z0-9]+/trigger'),
                        new RegExp(import.meta.env.VITE_API_BASE_URL + '/customers/book/token'),
                        new RegExp(import.meta.env.VITE_API_BASE_URL + '/customers/book/[A-Z0-9+]'),
                    ],
                    networkRequestHeaders: ['X-Custom-Header'],
                    networkResponseHeaders: ['X-Custom-Header'],
                }),
            ],

            // Set tracesSampleRate to 1.0 to capture 100%
            // of transactions for performance monitoring.
            // We recommend adjusting this value in production
            tracesSampleRate: 0.1,

            // Capture Replay for 10% of all sessions,
            // plus for 100% of sessions with an error
            replaysSessionSampleRate: 0.01,
            replaysOnErrorSampleRate: 1.0,

            ignoreErrors: [
                // Stripe Radar/hCaptcha posts to a child iframe via setTimeout; when the
                // frame is gone (backgrounded PaymentPage / unmounted element) Stripe
                // throws and Sentry captures it as unhandled. Card checkout still works.
                // MOVEPRO-BOOK-AA / CU-86d3whqfy
                'frame window is not ready',
            ],

            // Drop errors whose script URL is a browser extension (PayPal Honey Safari,
            // chrome-/moz-/safari-extension schemes). MOVEPRO-BOOK-AH.
            denyUrls: BROWSER_EXTENSION_URL_PATTERNS,

            // Axios interceptor rejects a stamped plain object after optionally
            // captureException'ing. Uncaught callers still trigger Sentry's
            // onunhandledrejection with that object (MOVEPRO-BOOK-AF "Network error"
            // noise; also BOOK-9Y/9Z). Drop those — the interceptor already decided.
            // Also drop empty `{}` rejections with no stack (MOVEPRO-BOOK-AG) — never
            // from Book's axios path, which always stamps `__bookProcessedError`.
            // Also drop browser-extension frames that slip past denyUrls (MOVEPRO-BOOK-AH,
            // e.g. when only the originalException.stack carries the Honey path).
            beforeSend(event, hint) {
                if (shouldDropBookProcessedSentryEvent(hint)) {
                    return null;
                }
                if (shouldDropBrowserExtensionSentryEvent(event, hint)) {
                    return null;
                }
                return event;
            },
        });
    }
}

/*
 * Set the user context for Sentry
 * @param {string} orderRef - The order reference
 * @param {string} email - The email address
 * @returns {void}
 */
function setUser({ orderRef, email }) {
    if (
        import.meta.env.VITE_ENV == 'production' ||
        import.meta.env.VITE_ENV == 'testing' ||
        import.meta.env.VITE_ENV == 'development'
    ) {
        Sentry.setUser({
            id: orderRef,
            email: email,
        });
    }
}

export { createSentry, setUser };
